import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useBrandingColors } from '../../../hooks/useBrandingColors';
import type { RequirementItem } from './types';
import editIcon from '../../../assets/icons/edit-icon.png';
import deleteIcon from '../../../assets/icons/delete-icon.png';
import dragHandleIcon from '../../../assets/icons/drag-handle.png';
import editCancelIcon from '../../../assets/icons/edit-cancel.png';
import editAcceptIcon from '../../../assets/icons/edit-accept.png';

interface RequirementsSectionProps {
  required: RequirementItem[];
  optional: RequirementItem[];
  onUpdate?: (required: RequirementItem[], optional: RequirementItem[]) => void;
  /** When true, hide all editing affordances (buttons, drag, inline edit). View-only. */
  readOnly?: boolean;
}

const TEMPLATE_ICONS: Record<string, React.ReactNode> = {
  'Возраст': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="6" r="3" stroke="#868C98" strokeWidth="1.4"/><path d="M4 17c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round"/></svg>
  ),
  'Пол': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="8" cy="6" r="3" stroke="#868C98" strokeWidth="1.4"/><path d="M2 17c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round"/><circle cx="15" cy="5" r="2" stroke="#868C98" strokeWidth="1.4"/><path d="M18 14c0-2.2-1.3-4-3-4s-3 1.8-3 4" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round"/></svg>
  ),
  'Гражданство': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7.5" stroke="#868C98" strokeWidth="1.4"/><ellipse cx="10" cy="10" rx="3" ry="7.5" stroke="#868C98" strokeWidth="1.4"/><path d="M3 7.5h14M3 12.5h14" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round"/></svg>
  ),
  'Зарплата': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="4" width="16" height="12" rx="2" stroke="#868C98" strokeWidth="1.4"/><path d="M2 8h16" stroke="#868C98" strokeWidth="1.4"/><circle cx="10" cy="12" r="1.5" stroke="#868C98" strokeWidth="1.4"/></svg>
  ),
  'Образование': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 3L2 7.5l8 4.5 8-4.5L10 3z" stroke="#868C98" strokeWidth="1.4" strokeLinejoin="round"/><path d="M5 9.5v4.5c0 1.5 2.2 3 5 3s5-1.5 5-3V9.5" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  'Опыт работы': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="6" width="16" height="11" rx="2" stroke="#868C98" strokeWidth="1.4"/><path d="M7 6V4a1 1 0 011-1h4a1 1 0 011 1v2" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round"/><path d="M2 11h16" stroke="#868C98" strokeWidth="1.4"/></svg>
  ),
  'Формат трудоустройства': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="2" width="14" height="16" rx="2" stroke="#868C98" strokeWidth="1.4"/><path d="M7 6h6M7 10h6M7 14h3" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round"/></svg>
  ),
  'График и место работы': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7.5" stroke="#868C98" strokeWidth="1.4"/><path d="M10 5.5V10l3.5 2" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  'Готовность к переезду': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 16l5-14 3 6h5l-9 8 2-6H4z" stroke="#868C98" strokeWidth="1.4" strokeLinejoin="round"/></svg>
  ),
  'Наличие документов': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M6 2h6l5 5v10a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" stroke="#868C98" strokeWidth="1.4" strokeLinejoin="round"/><path d="M12 2v5h5" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 12l2 2 3-4" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  'Опыт или образование': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="6" r="3" stroke="#868C98" strokeWidth="1.4"/><path d="M4 17c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round"/><path d="M14 3l2 1.5L14 6" stroke="#868C98" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
  ),
  'Подбор места работы': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 18s-6-5.3-6-9.5a6 6 0 1112 0C16 12.7 10 18 10 18z" stroke="#868C98" strokeWidth="1.4" strokeLinejoin="round"/><circle cx="10" cy="8.5" r="2" stroke="#868C98" strokeWidth="1.4"/></svg>
  ),
};

const REQUIREMENT_TEMPLATES = [
  'Возраст', 'Пол', 'Гражданство', 'Зарплата',
  'Образование', 'Опыт работы', 'Формат трудоустройства', 'График и место работы',
  'Готовность к переезду', 'Наличие документов', 'Опыт или образование', 'Подбор места работы',
];

const DEFAULT_WEIGHTS: Record<string, number> = {
  required: 7,
  optional: 3,
};

export function RequirementsSection({ required, optional, onUpdate, readOnly = false }: RequirementsSectionProps) {
  const colors = useBrandingColors();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAddTypeMenu, setShowAddTypeMenu] = useState(false);
  const [fadingOutId, setFadingOutId] = useState<string | null>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const addMenuRef = useRef<HTMLDivElement>(null);
  type ReqType = 'required' | 'optional';
  const dragItemRef = useRef<{ type: ReqType; index: number } | null>(null);
  const dragOverItemRef = useRef<{ type: ReqType; index: number } | null>(null);

  useEffect(() => {
    if (!showAddTypeMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        addMenuRef.current && !addMenuRef.current.contains(e.target as Node) &&
        addButtonRef.current && !addButtonRef.current.contains(e.target as Node)
      ) {
        setShowAddTypeMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddTypeMenu]);

  const handleDragStart = (type: ReqType, index: number, id: string) => {
    dragItemRef.current = { type, index };
    setDraggedId(id);
  };

  const handleDragOver = (e: React.DragEvent, type: ReqType, index: number, id: string) => {
    e.preventDefault();
    dragOverItemRef.current = { type, index };
    setDragOverId(id);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDrop = () => {
    if (!onUpdate || !dragItemRef.current || !dragOverItemRef.current) return;

    const from = dragItemRef.current;
    const to = dragOverItemRef.current;

    const lists: Record<ReqType, RequirementItem[]> = {
      required: [...required],
      optional: [...optional],
    };

    if (from.type === to.type) {
      // Same list reorder
      if (from.index === to.index) { handleDragEnd(); return; }
      const [moved] = lists[from.type].splice(from.index, 1);
      lists[from.type].splice(to.index, 0, moved);
    } else {
      // Cross-list move — update weight to target list default
      const [moved] = lists[from.type].splice(from.index, 1);
      moved.weight = DEFAULT_WEIGHTS[to.type];
      lists[to.type].splice(to.index, 0, moved);
    }

    onUpdate(lists.required, lists.optional);

    dragItemRef.current = null;
    dragOverItemRef.current = null;
    handleDragEnd();
  };

  const handleDelete = (id: string, type: ReqType) => {
    if (!onUpdate) return;
    setFadingOutId(id);
    setTimeout(() => {
      const r = type === 'required' ? required.filter(x => x.id !== id) : [...required];
      const o = type === 'optional' ? optional.filter(x => x.id !== id) : [...optional];
      onUpdate(r, o);
      setFadingOutId(null);
    }, 200);
  };

  const handleStartEdit = (item: RequirementItem) => {
    setEditingId(item.id);
    setEditText(item.text);
  };

  const handleSaveEdit = (type: ReqType) => {
    if (!onUpdate || !editingId) return;
    const updateFn = (items: RequirementItem[]) =>
      items.map(r => r.id === editingId ? { ...r, text: editText } : r);
    const r = type === 'required' ? updateFn(required) : [...required];
    const o = type === 'optional' ? updateFn(optional) : [...optional];
    onUpdate(r, o);
    setEditingId(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    // If the item was new (empty text), delete it on cancel
    if (editingId && onUpdate) {
      const reqItem = required.find(r => r.id === editingId);
      const optItem = optional.find(r => r.id === editingId);
      if (reqItem && !reqItem.text) {
        onUpdate(required.filter(r => r.id !== editingId), optional);
      } else if (optItem && !optItem.text) {
        onUpdate(required, optional.filter(r => r.id !== editingId));
      }
    }
    setEditingId(null);
    setEditText('');
  };

  const handleAddRequirement = (type: ReqType) => {
    if (!onUpdate) return;
    const newItem: RequirementItem = { id: `req-${Date.now()}`, text: '', weight: DEFAULT_WEIGHTS[type] };
    if (type === 'required') {
      onUpdate([...required, newItem], optional);
    } else {
      onUpdate(required, [...optional, newItem]);
    }
    setEditingId(newItem.id);
    setEditText('');
    setShowAddTypeMenu(false);
  };

  const handleSelectTemplate = (label: string) => {
    if (!onUpdate) return;
    const newItem: RequirementItem = { id: `req-${Date.now()}`, text: label, weight: DEFAULT_WEIGHTS.required };
    onUpdate([...required, newItem], optional);
    setShowTemplates(false);
  };

  const handleWeightChange = (id: string, type: ReqType, newWeight: number) => {
    if (!onUpdate) return;
    const clampedWeight = Math.min(10, Math.max(0, newWeight));
    const updateFn = (items: RequirementItem[]) =>
      items.map(r => r.id === id ? { ...r, weight: clampedWeight } : r);
    const r = type === 'required' ? updateFn(required) : [...required];
    const o = type === 'optional' ? updateFn(optional) : [...optional];
    onUpdate(r, o);
  };

  const renderItem = (item: RequirementItem, type: ReqType, index: number) => {
    const isEditing = editingId === item.id;
    const weight = item.weight ?? DEFAULT_WEIGHTS[type];

    if (isEditing) {
      return (
        <div key={item.id} className="py-2 ai-fade-in">
          <div className="flex items-start gap-2">
            <img src={dragHandleIcon} alt="" className="w-3 h-3 shrink-0 brightness-0 opacity-80 mt-3" />
            <div className="flex-1">
              <div className="px-3 py-2.5 bg-white rounded-[8px] border border-[#e2e4e9]">
                <input
              autoFocus
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveEdit(type);
                if (e.key === 'Escape') handleCancelEdit();
              }}
              onBlur={() => {
                // Commit pending text when focus leaves so clicking Save outside the panel
                // persists the edit even without pressing Enter.
                if (editText.trim()) handleSaveEdit(type);
                else handleCancelEdit();
              }}
              className="w-full text-[14px] bg-transparent outline-none text-[#0a0d14]"
              placeholder="Введите требование..."
            />
              </div>
              <div className="flex items-center justify-end gap-1.5 mt-1.5">
                <button onClick={handleCancelEdit} className="shrink-0 hover:opacity-80 transition-opacity">
                  <img src={editCancelIcon} alt="Отменить" className="w-6 h-6" />
                </button>
                <button
                  onClick={() => editText.trim() && handleSaveEdit(type)}
                  disabled={!editText.trim()}
                  className={`shrink-0 transition-opacity ${editText.trim() ? 'hover:opacity-80' : 'opacity-30 cursor-not-allowed'}`}
                >
                  <img src={editAcceptIcon} alt="Сохранить" className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={item.id}
        className={`flex items-center justify-between py-2.5 px-3 min-h-[44px] group rounded-[8px] bg-white border border-[#e2e4e9] border-l-[3px] transition-all duration-200 hover:shadow-sm hover:border-[#d4d7dc] ${
          type === 'required' ? 'border-l-[#E16349]' : 'border-l-[#375DFB]'
        } ${
          readOnly ? '' : 'cursor-grab active:cursor-grabbing'
        } ${
          fadingOutId === item.id ? 'opacity-0 scale-95' : ''
        } ${
          draggedId === item.id ? 'opacity-40 scale-[0.98]' : ''
        } ${
          dragOverId === item.id && draggedId !== item.id ? 'bg-[#f0f1f3] ring-2 ring-[#d76958]' : ''
        }`}
        draggable={!readOnly}
        onDragStart={readOnly ? undefined : () => handleDragStart(type, index, item.id)}
        onDragOver={readOnly ? undefined : (e) => handleDragOver(e, type, index, item.id)}
        onDragEnd={readOnly ? undefined : handleDragEnd}
        onDrop={readOnly ? undefined : handleDrop}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {!readOnly && (
            <img src={dragHandleIcon} alt="" className="w-3 h-3 shrink-0 brightness-0 opacity-50 group-hover:opacity-100 transition-all cursor-grab active:cursor-grabbing" />
          )}
          <span className="text-[14px] leading-[18px] text-[#0a0d14] whitespace-normal [overflow-wrap:anywhere]">{item.text}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Weight badge */}
          <div className="flex items-center gap-1">
            {!readOnly && (
              <button
                onClick={(e) => { e.stopPropagation(); handleWeightChange(item.id, type, weight - 1); }}
                className="w-7 h-7 flex items-center justify-center text-[14px] text-[#868C98] hover:text-[#0a0d14] hover:bg-[#f0f1f3] rounded-md transition-colors"
                title="Уменьшить вес"
              >
                -
              </button>
            )}
            <span
              className="inline-flex items-center justify-center min-w-[28px] h-[24px] px-2 text-[13px] font-medium rounded-full bg-[#f0f1f3] text-[#525866] select-none"
              title={`Вес: ${weight}`}
            >
              {weight}
            </span>
            {!readOnly && (
              <button
                onClick={(e) => { e.stopPropagation(); handleWeightChange(item.id, type, weight + 1); }}
                className="w-7 h-7 flex items-center justify-center text-[14px] text-[#868C98] hover:text-[#0a0d14] hover:bg-[#f0f1f3] rounded-md transition-colors"
                title="Увеличить вес"
              >
                +
              </button>
            )}
          </div>
          {!readOnly && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleStartEdit(item)}
                className="w-6 h-6 flex items-center justify-center text-[#868C98] hover:text-[#525866]"
              >
                <img src={editIcon} alt="Редактировать" className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleDelete(item.id, type)}
                className="w-6 h-6 flex items-center justify-center text-[#868C98] hover:text-[#e53935]"
              >
                <img src={deleteIcon} alt="Удалить" className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => (
    <div className="px-4 pb-4">
      {/* Required */}
      <div className="mb-3">
        <div
          className="flex items-center gap-2 mb-1"
          onDragOver={readOnly ? undefined : (e) => { e.preventDefault(); dragOverItemRef.current = { type: 'required', index: required.length }; setDragOverId('header-required'); }}
          onDrop={readOnly ? undefined : handleDrop}
        >
          <span className="text-[12px] font-medium text-[#E16349] bg-[#FFF0ED] px-2 py-0.5 rounded-full">Обязательные</span>
          <span className="text-[12px] text-[#868C98]">{required.length}</span>
          <div className="flex-1 h-px bg-[#e2e4e9]" />
        </div>
        {required.length > 0
          ? <div className="space-y-1.5 mt-1.5">{required.map((item, idx) => renderItem(item, 'required', idx))}</div>
          : readOnly
            ? <div className="text-[12px] text-[#868c98] py-2 px-1">Нет требований</div>
            : <div
                className="text-[12px] text-[#868c98] py-2 px-1"
                onDragOver={(e) => { e.preventDefault(); dragOverItemRef.current = { type: 'required', index: 0 }; setDragOverId('header-required'); }}
                onDrop={handleDrop}
              >
                Перетащите сюда требования
              </div>
        }
      </div>

      {/* Optional */}
      <div className="mb-3">
        <div
          className="flex items-center gap-2 mb-1"
          onDragOver={readOnly ? undefined : (e) => { e.preventDefault(); dragOverItemRef.current = { type: 'optional', index: optional.length }; setDragOverId('header-optional'); }}
          onDrop={readOnly ? undefined : handleDrop}
        >
          <span className="text-[12px] font-medium text-[#375DFB] bg-[#EBF1FF] px-2 py-0.5 rounded-full">Дополнительные</span>
          <span className="text-[12px] text-[#868C98]">{optional.length}</span>
          <div className="flex-1 h-px bg-[#e2e4e9]" />
        </div>
        {optional.length > 0
          ? <div className="space-y-1.5 mt-1.5">{optional.map((item, idx) => renderItem(item, 'optional', idx))}</div>
          : readOnly
            ? <div className="text-[12px] text-[#868c98] py-2 px-1">Нет требований</div>
            : <div
                className="text-[12px] text-[#868c98] py-2 px-1"
                onDragOver={(e) => { e.preventDefault(); dragOverItemRef.current = { type: 'optional', index: 0 }; setDragOverId('header-optional'); }}
                onDrop={handleDrop}
              >
                Перетащите сюда требования
              </div>
        }
      </div>

      {/* Actions */}
      {!readOnly && (
      <div className="relative flex items-center gap-3 mt-2 pt-2 border-t border-[#f0f1f3]">
        <button
          ref={addButtonRef}
          onClick={() => setShowAddTypeMenu(!showAddTypeMenu)}
          className="flex items-center gap-1 text-[13px] font-medium"
          style={{ color: colors.primary }}
        >
          + Добавить своё требование
        </button>
        {showAddTypeMenu && (
          <div
            ref={addMenuRef}
            className="absolute left-0 bottom-full mb-1 bg-white rounded-[10px] border border-[#e2e4e9] shadow-lg py-1 z-10 min-w-[200px] ai-fade-in"
          >
            <button
              onClick={() => handleAddRequirement('required')}
              className="w-full px-3 py-2 text-left text-[13px] text-[#0a0d14] hover:bg-[#f9fafb] transition-colors flex items-center gap-2"
            >
              <span className="w-2 h-2 rounded-full bg-[#E16349]" />
              Обязательное
            </button>
            <button
              onClick={() => handleAddRequirement('optional')}
              className="w-full px-3 py-2 text-left text-[13px] text-[#0a0d14] hover:bg-[#f9fafb] transition-colors flex items-center gap-2"
            >
              <span className="w-2 h-2 rounded-full bg-[#375DFB]" />
              Дополнительное
            </button>
          </div>
        )}
        {/* Шаблоны button hidden for now */}
      </div>
      )}
    </div>
  );

  return (<>
    <div className="space-y-0">
      {renderContent()}
    </div>

    {/* Templates modal */}
    {showTemplates && ReactDOM.createPortal(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
        onClick={() => setShowTemplates(false)}
      >
        <div
          className="bg-white rounded-[16px] shadow-xl p-8 w-[560px] max-h-[80vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-[18px] font-medium text-[#0a0d14]">Выберите шаблон</h3>
            <button
              onClick={() => setShowTemplates(false)}
              className="text-[#868C98] hover:text-[#0a0d14] transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {REQUIREMENT_TEMPLATES.map((label) => (
              <button
                type="button"
                key={label}
                onClick={() => handleSelectTemplate(label)}
                className="flex items-center gap-3 px-4 py-3 rounded-[12px] border border-[#e2e4e9] text-left hover:bg-[#f9fafb] hover:border-[#c5c8d0] transition-colors cursor-pointer"
              >
                <span className="shrink-0 w-9 h-9 flex items-center justify-center rounded-[8px] bg-[#f2f0ed] pointer-events-none">{TEMPLATE_ICONS[label]}</span>
                <span className="text-[14px] text-[#0a0d14] pointer-events-none">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>,
      document.body
    )}
  </>);
}
